import { User } from '../../models/User'
import React, { useCallback, useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import { Modal } from '../../components/Modal/Modal'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import {
  useDeactivateUserByIdMutation,
  useDeleteUserShareSettingsMutation,
  useGetUserByIdQuery,
  useSaveUserByIdMutation,
  useSaveUserShareSettingsMutation,
} from '../../store/user/user'
import { DEFAULT_SESSION_SHARING_ENABLED, useGetAccountUserSettingsQuery } from '../../store/accountUserSettings/accountUserSettings'
import { getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils' // TODO: move to user utils
import { addToast } from '../../store/toast/toastSlice'
import { UserShareForm } from '../ProfileSettings/Collaboration/CollaborationTab'

interface DeactivateUserModalProps {
  user: User
  onHide: () => void
  onUserDeactivated: () => void
}

export const DeactivateUserModal: React.FC<DeactivateUserModalProps> = ({ user, onHide, onUserDeactivated }) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const [deactivateUser] = useDeactivateUserByIdMutation()
  const [deactivationInProgress, setDeactivationInProgress] = useState(false)
  const [saveUserById] = useSaveUserByIdMutation()
  const [deleteUserShareSettings] = useDeleteUserShareSettingsMutation()
  const [saveUserShareSettings] = useSaveUserShareSettingsMutation()
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])

  const { data: userData, isFetching: userLoading } = useGetUserByIdQuery({ accountId, userId: user.id }, { refetchOnMountOrArgChange: true })

  const { data: accountUserSettings, isFetching: accountUserSettingsLoading } = useGetAccountUserSettingsQuery(
    { accountId },
    { refetchOnMountOrArgChange: true },
  )

  const [sharingEnabled, setSharingEnabled] = useState<boolean>(true)

  useEffect(() => {
    if (!userData || !accountUserSettings) {
      return
    }
    const userSetting = userData.data.settings
    const isUserSharingEnabled = !!(userSetting && userSetting.contactSharingEnabled && userSetting.sessionSharingEnabled)
    const isAccountSharingEnabled = !!(
      accountUserSettings.data &&
      accountUserSettings.data.settingsOverride &&
      accountUserSettings.data.settingsOverride.sessionSharingEnabled &&
      accountUserSettings.data.settingsOverride.contactSharingEnabled
    )

    if (userSetting) {
      setSharingEnabled(isUserSharingEnabled)
    } else if (accountUserSettings.data?.settingsOverride) {
      setSharingEnabled(isAccountSharingEnabled)
    } else {
      setSharingEnabled(DEFAULT_SESSION_SHARING_ENABLED)
    }
  }, [userData, accountUserSettings])

  const handleSharingEnabledChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSharingEnabled(e.currentTarget.checked)
  }

  const handleDeactivateUser = useCallback(async () => {
    try {
      setDeactivationInProgress(true)
      const currentSettings = userData?.data.settings
      await saveUserById({
        accountId,
        userId: user.id,
        userData: { settings: { ...currentSettings, sessionSharingEnabled: sharingEnabled, contactSharingEnabled: sharingEnabled } },
      })
      if (sharingEnabled) {
        await saveUserShareSettings({
          accountId,
          userId: user.id,
          payload: { shareType: 'meeting', sharedWithUserIds: selectedUserIds },
        })
        await saveUserShareSettings({
          accountId,
          userId: user.id,
          payload: { shareType: 'contact', sharedWithUserIds: selectedUserIds },
        })
      } else {
        await deleteUserShareSettings({ accountId, userId: user.id, shareType: 'meeting' })
        await deleteUserShareSettings({ accountId, userId: user.id, shareType: 'contact' })
      }
      await deactivateUser({ accountId, userId: user.id }).unwrap()
      dispatch(addToast({ variant: 'success', title: 'User deactivated', messages: [`${getUserDisplayName(user)} has been deactivated.`] }))
      setDeactivationInProgress(false)
      onUserDeactivated()
    } catch {
      dispatch(addToast({ variant: 'error', title: 'Deactivation failed', messages: ['Failed to deactivate user. Please try again.'] }))
    } finally {
      setDeactivationInProgress(false)
    }
  }, [
    user,
    onUserDeactivated,
    deactivateUser,
    saveUserById,
    saveUserShareSettings,
    deleteUserShareSettings,
    accountId,
    dispatch,
    sharingEnabled,
    selectedUserIds,
    userData,
  ])

  if (!user) {
    return null
  }

  const displayName = getUserDisplayName(user)

  const body = (
    <div className='d-flex flex-column gap-4'>
      <p className='paragraph-medium mb-0'>
        By deactivating <strong>{displayName}</strong>, their account <strong>will be permanently disabled </strong>
        and their seat will be released. This <strong>cannot be undone</strong>.
      </p>

      <div className='d-flex flex-column gap-3'>
        <p className='paragraph-medium fw-bold mb-0'>Share access to user data (optional)</p>
        <p className='paragraph-medium mb-0'>
          Give another team members access to {displayName}'s <strong>past meetings and contacts</strong>. They can view and edit this data, but they don't gain
          ownership. You can also manage sharing options of deactivated users later.
        </p>
        <div className='d-flex justify-content-between'>
          <span className='paragraph-medium'>Share past meetings and contacts with team members</span>
          <Form.Switch disabled={userLoading || accountUserSettingsLoading} checked={sharingEnabled} onChange={handleSharingEnabledChanged} />
        </div>
        <div>
          {sharingEnabled && (
            <UserShareForm
              shareType={'meeting'}
              attachedShareTypes={['contact']}
              noSelectedUserText={`Share access with all team members`}
              multiSelectWarningText={`If no specific users are selected data will be shared with all team members.`}
              hideSaveButton
              onSelectedUsersChange={setSelectedUserIds}
              userId={user.id}
              showInModal={true}
            />
          )}
        </div>
      </div>

      <a
        href='https://help.zocks.io/en/articles/13742340-user-management'
        target='_blank'
        rel='noopener noreferrer'
        className='paragraph-small fw-bold text-decoration-underline zocks-text-gray-900'
      >
        Read more about deactivation in Help Center
      </a>
    </div>
  )

  return (
    <Modal
      show
      centered={true}
      onHide={onHide}
      header={<h4>Deactivate User</h4>}
      headerBodySeparator
      body={body}
      showFooterActions={true}
      onFooterPrimaryAction={handleDeactivateUser}
      footerPrimaryActionLoading={deactivationInProgress}
      footerPrimaryActionDisabled={userLoading || accountUserSettingsLoading}
      footerPrimaryActionTitle='Deactivate'
      footerPrimaryActionButtonVariant='primary'
      footerSecondaryActionDisabled={deactivationInProgress}
    />
  )
}
